import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonIcon } from "@ionic/react";
import { chatbubbleOutline, timeOutline } from "ionicons/icons";
import {
  PostEngagementProps,
  ReactionType,
  REACTION_LABELS,
} from "../../types/socialJournal";
import ReactionPicker from "./ReactionPicker";
import "./PostEngagement.css";
import {
  ThumbsUpIcon,
  LoveIcon,
  InsightfulIcon,
  ClapIcon,
  SmileIcon,
  FireIcon,
} from "../../assets/icons";
import useHaptics from "../../hooks/useHaptics";
const REACTION_EMOJIS: Record<
  ReactionType,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  thumbsUp: ThumbsUpIcon,
  love: LoveIcon,
  insightful: InsightfulIcon,
  clap: ClapIcon,
  smile: SmileIcon,
  fire: FireIcon, // You might need to add a FireIcon to your assets
};
const PostEngagement: React.FC<PostEngagementProps> = ({
  reactions,
  replies,
  userReaction,
  timestamp,
  onReact,
  onReply,
}) => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const activePointerId = useRef<number | null>(null);
  const pickerOpenedByLongPress = useRef(false);
  const pressOrigin = useRef<{ x: number; y: number } | null>(null);

  // Travel past this and the gesture is a scroll, not a long press.
  const LONG_PRESS_MOVE_TOLERANCE = 10;
  const { successActivity, buttonPress } = useHaptics();
  const [isAnimating, setIsAnimating] = useState(false);
  const likeButtonRef = useRef<HTMLIonButtonElement>(null);
  const animationTimer = useRef<number | null>(null);
  const isMounted = useRef(true);

  // Default reaction when user hasn't reacted yet
  const DEFAULT_REACTION: ReactionType = "thumbsUp";

  // Reaction background colors
  const reactionColors: Record<ReactionType, string> = {
    thumbsUp: "#E1F2FE",
    love: "#E4F6FB",
    insightful: "#FFF0E0",
    clap: "#E0FFF4",
    smile: "#EEEBF4",
    fire: "#FFE0E3",
  };

  // Calculate total reactions
  const getTotalReactions = () => {
    return Object.values(reactions).reduce((total, count) => total + count, 0);
  };
  const getTopReactions = () => {
    return Object.entries(reactions)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([reaction]) => reaction as ReactionType);
  };

  // Handle quick tap - toggle reaction on/off
  const handleLikePress = () => {
    setIsAnimating(true);

    if (userReaction) {
      // Remove current reaction
      onReact(userReaction);
    } else {
      // Add default reaction
      onReact(DEFAULT_REACTION);
    }
    successActivity();

    // Clear any existing animation timer
    if (animationTimer.current) {
      clearTimeout(animationTimer.current);
    }

    animationTimer.current = window.setTimeout(() => {
      if (isMounted.current) {
        setIsAnimating(false);
      }
    }, 400);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Handle pointer down - start long press timer and capture pointer
  const handlePointerDown = (e: React.PointerEvent) => {
    // Suppress the OS text-selection gesture, which would pointercancel us
    // before the picker opens. Panning is unaffected (that's `touch-action`).
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointerId.current = e.pointerId;
    pickerOpenedByLongPress.current = false;
    pressOrigin.current = { x: e.clientX, y: e.clientY };

    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      setShowReactionPicker(true);
      pickerOpenedByLongPress.current = true;
      buttonPress();
    }, 350);
  };

  // A drag past the tolerance is a scroll, so drop the pending long press.
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!longPressTimer.current || !pressOrigin.current) return;

    const dx = Math.abs(e.clientX - pressOrigin.current.x);
    const dy = Math.abs(e.clientY - pressOrigin.current.y);

    if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
      cancelLongPress();
      pressOrigin.current = null;
    }
  };

  // Handle pointer up - cancel timer or trigger quick reaction
  const handlePointerUp = (e: React.PointerEvent) => {
    const wasPendingTap = longPressTimer.current !== null;
    cancelLongPress();
    pressOrigin.current = null;

    // Timer still pending = finger came up early and didn't wander: a tap.
    if (wasPendingTap && !pickerOpenedByLongPress.current) {
      handleLikePress();
    }

    // This button owns the pointer for the whole gesture (the picker listens
    // on document instead of capturing), so it always releases.
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Safely ignore if already released
    }
    activePointerId.current = null;
  };

  // Handle pointer cancel - cleanup
  const handlePointerCancel = (e: React.PointerEvent) => {
    cancelLongPress();
    pressOrigin.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Safely ignore if already released
    }
    activePointerId.current = null;
  };

  // While the picker is open the gesture is reaction selection, not scrolling.
  // `touch-action` latches at gesture start, so blocking a scroll mid-gesture
  // needs a non-passive touchmove listener. Attached once, gated on the ref.
  useEffect(() => {
    const el = likeButtonRef.current;
    if (!el) return;

    const blockScrollWhilePicking = (e: TouchEvent) => {
      if (pickerOpenedByLongPress.current && e.cancelable) {
        e.preventDefault();
      }
    };

    el.addEventListener("touchmove", blockScrollWhilePicking, {
      passive: false,
    });

    return () => {
      el.removeEventListener("touchmove", blockScrollWhilePicking);
    };
  }, []);

  // Handle reaction selection from picker
  const handleReactionSelect = (reaction: ReactionType) => {
    setIsAnimating(true);
    onReact(reaction);
    setShowReactionPicker(false);
    activePointerId.current = null;

    // Clear any existing animation timer
    if (animationTimer.current) {
      clearTimeout(animationTimer.current);
    }

    animationTimer.current = window.setTimeout(() => {
      if (isMounted.current) {
        setIsAnimating(false);
      }
    }, 400);
  };

  // Get current display state
  const displayReaction = userReaction || DEFAULT_REACTION;
  const isActive = !!userReaction;

  // Get the current reaction icon component
  const CurrentReactionIcon = REACTION_EMOJIS[displayReaction];

  // Cleanup timers on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      if (animationTimer.current) {
        clearTimeout(animationTimer.current);
      }
    };
  }, []);

  return (
    <div className="post-engagement" style={{ position: "relative" }}>
      {/* Engagement Stats */}
      <div className="engagement-stats p-3">
        <div className="stats-left">
          {getTotalReactions() > 0 && (
            <span className="reaction-count">{getTotalReactions()}</span>
          )}
          <div className="reaction-emojis">
            {getTopReactions().map((reaction, index) => {
              const ReactionIcon = REACTION_EMOJIS[reaction];
              if (!ReactionIcon) return null;
              return (
                <ReactionIcon
                  key={`${reaction}-${index}`}
                  className="reaction-emoji-display"
                  aria-label={reaction}
                />
              );
            })}
          </div>
        </div>
        <div className="stats-right cursor-pointer" onClick={onReply}>
          <span className="reply-count">
            {replies > 0
              ? `${replies} Comment${replies > 1 ? "s" : ""}`
              : "No Comments"}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons flex justify-between items-center md:text-lg">
        <IonButton
          ref={likeButtonRef}
          fill="clear"
          className={`action-button like-button ${isActive ? "active" : ""} ${
            isAnimating ? "transitioning" : ""
          }`}
          data-reaction={displayReaction}
          style={{
            backgroundColor: isActive
              ? reactionColors[displayReaction]
              : "transparent",
            transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            // pan-y, not none: the button spans the card, so the feed must
            // still scroll when a swipe starts on it.
            touchAction: "pan-y",
            // pan-y can make pointerdown non-cancelable, so preventDefault
            // alone can't stop text selection. These do.
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
          }}
          onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          {CurrentReactionIcon && (
            <CurrentReactionIcon
              className="reaction-emoji-btn"
              aria-label={REACTION_LABELS[displayReaction]}
            />
          )}
          <span className="action-label">
            {REACTION_LABELS[displayReaction]}
          </span>
        </IonButton>

        <IonButton
          fill="clear"
          className="action-button reply-button"
          onClick={onReply}
        >
          <IonIcon icon={chatbubbleOutline} className="action-icon" />
          <span className="text-xls md:text-base text-black">Comment</span>
        </IonButton>

        {/* ✅ FIXED: Display timestamp directly (backend already formats it) */}
        <div className="flex item-2 gap-x-1 me-1 items-center">
          <IonIcon icon={timeOutline} className="time-icon text-gray-500" />
          <span className="text-xls md:text-base text-gray-600">
            {timestamp}
          </span>
        </div>
      </div>

      {/* Reaction Picker */}
      <ReactionPicker
        isOpen={showReactionPicker}
        onReactionSelect={handleReactionSelect}
        onClose={() => {
          setShowReactionPicker(false);
          activePointerId.current = null;
        }}
        triggerRef={likeButtonRef}
        activePointerId={activePointerId.current}
      />
    </div>
  );
};

export default PostEngagement;
